/**
 * Rótulo "via …" no histórico — canal da interação (mensagem), não da solicitação.
 * Espelha xpro4api/src/utils/messageChannelDisplay.js
 */

import { CHANNEL_TYPES } from "../constants/channelCatalog";
import { looksLikeOrphanEmailAttachment } from "../components/MessageHistory/emailInboundAttachments";

const WHATSAPP_LM_TIPOS = new Set([3, 13, 14, 15, 16, 37, 35, 39]);
const EMAIL_RAW_TYPES = new Set(["email"]);
const PHONE_RAW_TYPES = new Set(["call", "phone", "telefone"]);
const WHATSAPP_RAW_TYPES = new Set(["whatsapp"]);
const WHATSAPP_ORIGIN_TIPOS = new Set([6, 20, 24]);

const ORIGIN_TIPO_TO_LABEL = {
  5: "Telefone",
  29: "Telefone",
  9: "Email",
  30: "Email",
  35: "Email",
  20: "WhatsApp",
  24: "WhatsApp",
  6: "WhatsApp",
  12: "Messenger",
  21: "Instagram",
  23: "Facebook Comentários",
  44: "Instagram Comentários",
  25: "SMS",
  31: "SMS",
  26: "App",
  27: "App",
  28: "Wordpress",
  32: "Ads",
  33: "Ads",
  40: "Ecommerce",
  43: "Portal do cliente",
  47: "Chat no site",
};

function isXproClienteOrigin(originTipo) {
  return Number(originTipo) === CHANNEL_TYPES.XPRO_CLIENTE;
}

function resolveXproClienteViaLabel(message) {
  const originTipo = message?.originTipo ?? message?.messageChannelTipo;
  if (!isXproClienteOrigin(originTipo)) return "";

  const channelName = (message?.channelName ?? "").toString().trim();
  return labelFromOriginTipo(originTipo) || channelName || "Portal do cliente";
}

function labelFromLmTipo(lmTipo) {
  const t = Number(lmTipo);
  if (!Number.isFinite(t)) return "";
  if (WHATSAPP_LM_TIPOS.has(t)) return "WhatsApp";
  if (t === 2) return "Email";
  if (t === 4) return "Telefone";
  if (t === 24) return "SMS";
  return "";
}

function labelFromOriginTipo(originTipo) {
  const t = Number(originTipo);
  if (!Number.isFinite(t)) return "";
  return ORIGIN_TIPO_TO_LABEL[t] || "";
}

function labelFromRawChannelType(rawType) {
  const lower = (rawType || "").toString().trim().toLowerCase();
  if (!lower) return "";
  if (WHATSAPP_RAW_TYPES.has(lower) || lower.startsWith("whatsapp")) return "WhatsApp";
  if (EMAIL_RAW_TYPES.has(lower)) return "Email";
  if (PHONE_RAW_TYPES.has(lower)) return "Telefone";
  if (lower === "sms") return "SMS";
  if (lower === "messenger") return "Messenger";
  if (lower === "instagram") return "Instagram";
  return "";
}

function isWhatsappRawType(rawType) {
  const lower = (rawType || "").toString().trim().toLowerCase();
  return WHATSAPP_RAW_TYPES.has(lower) || lower.startsWith("whatsapp");
}

/**
 * @param {object} message
 * @param {{ channelId?: number|string, channelLabel?: string }} [conversation]
 */
export function resolveMessageViaChannelLabel(message, conversation = {}) {
  if (conversation?.isChatbotSimulation || message?.isChatbotSimulation) {
    return "Simulador";
  }

  try {
    const raw = message?.json_whatsapp;
    const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
    if (j && j.xpro_transport === "webchat") {
      const originTipo = Number(message?.originTipo ?? message?.messageChannelTipo);
      if (originTipo === 47) return "Chat no site";
      return "Web Chat";
    }
    if (j && (j.xpro_transport === "twilio_voice" || j?.xpro_voice?.spoken)) return "Telefone";
  } catch {
    /* ignore */
  }

  const xproLabel = resolveXproClienteViaLabel(message);
  if (xproLabel) return xproLabel;

  if (looksLikeOrphanEmailAttachment(message)) {
    return "Email";
  }

  const channelName = (message?.channelName ?? "").toString().trim();
  const channel = (message?.channel ?? "").toString().trim();
  const originTipo = message?.originTipo;
  const msgChannelId = message?.messageChannelId;
  const leadChannelId = conversation?.channelId;
  const rawType = (message?.rawChannelType ?? "").toString().trim().toLowerCase();

  const interactionLabel = labelFromRawChannelType(rawType);
  const originLabel = labelFromOriginTipo(originTipo) || labelFromRawChannelType(channel);
  const interactionFamily = interactionLabel ? interactionLabel.toLowerCase() : "";
  const originFamily = originLabel ? originLabel.toLowerCase() : "";
  const isEmailOrigin = [9, 30, 35].includes(Number(originTipo));

  const differentFromLead =
    msgChannelId != null &&
    leadChannelId != null &&
    Number(msgChannelId) !== Number(leadChannelId);

  // Omnichannel: mensagem entrou por outro canal — mostrar o nome do canal de entrada
  if (differentFromLead && channelName) {
    return channelName;
  }

  if (channelName) {
    const sameAsLeadOrigin =
      msgChannelId != null &&
      leadChannelId != null &&
      Number(msgChannelId) === Number(leadChannelId);

    if (sameAsLeadOrigin && interactionFamily && !isXproClienteOrigin(originTipo)) {
      const lmWhatsapp = isWhatsappRawType(rawType);
      // Instagram/Messenger gravam texto como leadsmensagens.tipo 3 (família WhatsApp).
      if (lmWhatsapp && originTipo != null && !WHATSAPP_ORIGIN_TIPOS.has(Number(originTipo))) {
        return channelName || originLabel || interactionLabel;
      }
      if (originFamily && interactionFamily !== originFamily) {
        if (isEmailOrigin) return channelName || interactionLabel || originLabel;
        return interactionLabel || originLabel || channelName;
      }
    }

    return channelName;
  }

  if (channel && !["unknown", "desconhecido", "xpro", "call", "note", "log"].includes(channel.toLowerCase())) {
    return channel;
  }

  if (isWhatsappRawType(rawType) && originTipo != null && !WHATSAPP_ORIGIN_TIPOS.has(Number(originTipo))) {
    return originLabel || interactionLabel || "";
  }

  return interactionLabel || originLabel || "";
}

/**
 * @param {object} message
 * @param {string} [conversationChannelLabel] — legado: rótulo do canal da solicitação (evitar como fallback)
 * @param {{ channelId?: number|string, channelLabel?: string }} [conversationContext]
 */
export function getMessageViaChannelLabel(message, conversationChannelLabel, conversationContext) {
  const ctx =
    conversationContext && typeof conversationContext === "object"
      ? conversationContext
      : { channelLabel: conversationChannelLabel };

  const msgType = (message?.type || "").toString().toLowerCase();
  if (msgType === "note" || msgType === "log" || msgType === "xpro") {
    return "";
  }

  const resolved = resolveMessageViaChannelLabel(message, {
    channelId: ctx.channelId,
    channelLabel: ctx.channelLabel,
  });
  if (resolved) return normalizeViaLabel(resolved);

  const raw = (message?.channelName ?? message?.channel ?? "").toString().trim();
  if (!raw) return "";

  const lower = raw.toLowerCase();
  if (lower === "unknown" || lower === "desconhecido" || lower === "xpro" || lower === "call") {
    return lower === "call" ? "Call" : "";
  }

  if (["imagem", "image", "video", "audio", "document"].includes(lower)) {
    return isWhatsappRawType(message?.rawChannelType) ? "WhatsApp" : "";
  }

  return normalizeViaLabel(raw);
}

function normalizeViaLabel(label) {
  const lower = label.toLowerCase();
  if (lower.startsWith("whatsapp")) return "WhatsApp";
  if (lower === "simulador") return "Simulador";
  if (lower.includes("portal cliente") || lower.includes("xpro cliente")) return "Portal do cliente";
  if (lower === "email") return "Email";
  if (lower === "sms") return "SMS";
  if (lower === "phone" || lower === "telefone") return "Telefone";
  return label;
}
